"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, ShieldCheck, Scale, BookOpen, CircleAlert as AlertCircle, Mail, ChevronRight, Printer, Download } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const legalSections = [{
  id: "acceptance-of-terms",
  number: "01",
  title: "Acceptance of terms",
  icon: FileText,
  summary: "By accessing or using MinfoMedia publications, research reports, datasets, and digital assets, you agree to comply with and be bound by these legal terms.",
  paragraphs: ["These Terms of Use constitute a legally binding agreement between you (whether individually or on behalf of an enterprise or institution) and MinfoMedia regarding your access to and use of our website, published research, whitepapers, digital archives, and related analytical services.", "If you do not agree with all of these Terms of Use, you are expressly prohibited from using the platform and must discontinue access immediately. Continued access constitutes unambiguous acceptance of the terms in effect at the time of your visit."]
}, {
  id: "intellectual-property",
  number: "02",
  title: "Intellectual property and reproduction rights",
  icon: ShieldCheck,
  summary: "All analytical reports, charts, proprietary benchmarks, articles, and graphic covers remain the exclusive intellectual property of MinfoMedia.",
  paragraphs: ["All original content, features, methodologies, editorial frameworks, and data visualizations published on MinfoMedia are protected by international copyright, trademark, trade secret, and other intellectual property or proprietary rights laws.", "You are granted a limited, non-exclusive, non-transferable, and revocable license to view, download, and print individual reports strictly for internal, non-commercial business analysis. Any broader republication, syndication, systematic data extraction, or commercial redistribution requires prior written authorization from our editorial board."]
}, {
  id: "content-disclaimer",
  number: "03",
  title: "Research and sample content disclaimers",
  icon: AlertCircle,
  summary: "Our publications provide independent market intelligence and editorial analysis; they do not constitute specific investment, legal, or financial advice.",
  paragraphs: ["The information and insights presented across MinfoMedia are prepared with rigorous research standards and peer review methodologies. However, all materials are provided on an 'as is' and 'as available' basis for general strategic reference.", "MinfoMedia makes no guarantees regarding the completeness, timeliness, or accuracy of third-party market data cited within historical or projected analyses. Executive decision-makers should independently evaluate strategic decisions in conjunction with qualified professional counsel."]
}, {
  id: "user-conduct",
  number: "04",
  title: "User conduct and acceptable use",
  icon: BookOpen,
  summary: "Users agree not to disrupt network infrastructure, scrape analytical datasets without permission, or impersonate editorial personnel.",
  paragraphs: ["When engaging with MinfoMedia, you agree not to use automated bots, crawlers, or scrapers to extract bulk content without an authorized enterprise API agreement. You may not circumvent authentication protocols or attempt unauthorized access to restricted research tiers.", "You further agree not to introduce malicious code, forge headers, or transmit unsolicited commercial messages through MinfoMedia contact channels or community discussion modules."]
}, {
  id: "limitation-of-liability",
  number: "05",
  title: "Limitation of liability and warranty disclaimer",
  icon: Scale,
  summary: "MinfoMedia and its editorial contributors are not liable for direct, indirect, or consequential damages arising from the use of published materials.",
  paragraphs: ["To the maximum extent permitted by applicable law, MinfoMedia, its affiliates, directors, researchers, and editorial staff shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of revenue, operational interruption, or data loss.", "We disclaim all warranties of any kind, whether express or implied, including the implied warranties of merchantability, fitness for a particular analytical purpose, and non-infringement."]
}, {
  id: "governing-law",
  number: "06",
  title: "Governing law and dispute resolution",
  icon: Scale,
  summary: "These terms are governed by the laws of Springfield, USA, without regard to its conflict of law principles.",
  paragraphs: ["Any dispute, controversy, or claim arising out of or relating to these Terms of Use or the breach, termination, or invalidity thereof shall be resolved through good-faith negotiation between the parties.", "If negotiations fail to achieve resolution within thirty (30) business days, the matter shall be submitted to binding arbitration in Springfield, USA, under standard commercial arbitration rules."]
}, {
  id: "modifications-contact",
  number: "07",
  title: "Modifications and contact information",
  icon: Mail,
  summary: "Terms may be updated periodically to reflect evolving regulatory frameworks or expanded research publications.",
  paragraphs: ["We reserve the right to modify these Terms of Use at any time. When material changes occur, we will update the 'Effective Date' at the head of this document and notify active subscribers through our verified newsletter channels.", "For legal inquiries, licensing permissions, or editorial compliance questions, please contact our legal desk at info@minfomedia.com or write to 123 Main Street, Springfield, USA."]
}];
export default function Terms() {
  const shouldReduceMotion = useReducedMotion();
  const [activeSection, setActiveSection] = useState<string>("acceptance-of-terms");
  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="terms-of-use" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule Motif */}
        <div className="mb-10 sm:mb-14">
          <div className="h-[2px] w-full bg-primary" />
          <div className="mt-1 h-[1px] w-full bg-border" />
        </div>

        {/* Section Header */}
        <div className="max-w-[840px] mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Legal agreement
            </span>
            <span className="inline-block h-1 w-1 rounded-full bg-muted-foreground" />
            <span className="text-xs font-mono text-muted-foreground">
              Document ref: MM-TOS-2025
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-6">
            Terms of use
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Please read these terms carefully before accessing MinfoMedia research, reports, and analytical services. They govern your rights, obligations, and access to all published content.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono text-muted-foreground">
            <div>
              <span className="font-semibold text-foreground">Effective date:</span> January 15, 2025
            </div>
            <span className="hidden sm:inline-block">•</span>
            <div>
              <span className="font-semibold text-foreground">Last revised:</span> October 2024
            </div>
            <span className="hidden sm:inline-block">•</span>
            <div>
              <span className="font-semibold text-foreground">Jurisdiction:</span> Springfield, USA
            </div>
          </div>
        </div>

        {/* Fast Action Toolbar */}
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-y border-border py-4">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
              Quick jump:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {legalSections.map((sec, i) => <Link key={sec.id} data-index={i} to={`#${sec.id}`} onClick={() => setActiveSection(sec.id)} className={`px-2.5 py-1 text-xs font-mono rounded-sm transition-[color,background-color,border-color] ${activeSection === sec.id ? "bg-primary text-primary-foreground font-semibold" : "bg-secondary text-secondary-foreground hover:bg-muted"}`}>
                  {sec.number}
                </Link>)}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} className="text-xs gap-1.5 h-8 border-border bg-card text-foreground hover:bg-muted">
              <Printer className="size-3.5" />
              <span>Print document</span>
            </Button>
            <Button variant="outline" size="sm" asChild className="text-xs gap-1.5 h-8 border-border bg-card text-foreground hover:bg-muted">
              <Link to="/PrivacyDataProtectionPage">
                <ShieldCheck className="size-3.5" />
                <span>Privacy policy</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Main Grid: 8-col content / 4-col sticky table of contents */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Main Legal Content */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.05
        }} className="lg:col-span-8 space-y-8 sm:space-y-10">
            {legalSections.map((section, index) => {
            const IconComp = section.icon;
            return <motion.article key={section.id} data-index={index} id={section.id} variants={itemVariants} className="scroll-mt-28">
                  <Card className="rounded-md border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-border/80">
                    <CardContent className="p-6 sm:p-8">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
                            Section {section.number}
                          </span>
                          <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                            {section.title}
                          </h2>
                        </div>
                        <div className="p-2 rounded-sm bg-muted text-muted-foreground shrink-0 hidden sm:flex">
                          <IconComp className="size-4" />
                        </div>
                      </div>

                      {/* Executive Summary callout box */}
                      <div className="mb-6 rounded-sm border-l-2 border-primary bg-secondary/40 p-4">
                        <p className="text-sm font-medium text-foreground leading-relaxed">
                          {section.summary}
                        </p>
                      </div>

                      {/* Full Editorial Paragraphs */}
                      <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {section.paragraphs.map((p, pIndex) => <p key={pIndex} data-index={pIndex}>
                            {p}
                          </p>)}
                      </div>
                    </CardContent>
                  </Card>
                </motion.article>;
          })}

            {/* Editorial Inquiry Note */}
            <Card className="rounded-md border border-border bg-muted/40 p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground mb-1">
                    Have questions regarding usage rights?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Our compliance desk reviews commercial licensing requests and syndication permissions.
                  </p>
                </div>
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
                  <a href="mailto:info@minfomedia.com">Contact legal desk</a>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Sticky Editorial Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-28 space-y-6">
              {/* Document Overview Card */}
              <Card className="rounded-md border border-border bg-card shadow-xs">
                <CardContent className="p-6">
                  <h3 className="font-serif text-base font-semibold text-foreground mb-4 pb-3 border-b border-border">
                    Table of contents
                  </h3>
                  <nav className="space-y-1">
                    {legalSections.map((sec, i) => <Link key={sec.id} data-index={i} to={`#${sec.id}`} onClick={() => setActiveSection(sec.id)} className={`group flex items-center justify-between px-3 py-2 text-xs rounded-sm transition-[background-color,color] ${activeSection === sec.id ? "bg-secondary text-foreground font-semibold" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}>
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground group-hover:text-foreground">
                            {sec.number}.
                          </span>
                          <span className="truncate">{sec.title}</span>
                        </span>
                        <ChevronRight className="size-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
                      </Link>)}
                  </nav>
                </CardContent>
              </Card>

              {/* Related Governance Documents */}
              <Card className="rounded-md border border-border bg-card shadow-xs">
                <CardContent className="p-6">
                  <h3 className="font-serif text-base font-semibold text-foreground mb-3">
                    Governance & ethics
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                    Explore related policy documentation covering data protection and research integrity.
                  </p>

                  <div className="space-y-2">
                    <Button variant="outline" asChild className="w-full justify-start text-xs h-9 border-border bg-background text-foreground hover:bg-muted">
                      <Link to="/PrivacyDataProtectionPage">
                        <ShieldCheck className="mr-2 size-3.5 text-muted-foreground" />
                        <span>Privacy & data protection</span>
                      </Link>
                    </Button>

                    <Button variant="outline" asChild className="w-full justify-start text-xs h-9 border-border bg-background text-foreground hover:bg-muted">
                      <Link to="/AboutUsPage">
                        <BookOpen className="mr-2 size-3.5 text-muted-foreground" />
                        <span>Editorial standards</span>
                      </Link>
                    </Button>

                    <Button variant="outline" asChild className="w-full justify-start text-xs h-9 border-border bg-background text-foreground hover:bg-muted">
                      <Link to="/ResearchReportsPage">
                        <Download className="mr-2 size-3.5 text-muted-foreground" />
                        <span>Research report archive</span>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Institutional Notice Box */}
              <div className="rounded-md border border-border bg-muted/40 p-5 text-xs text-muted-foreground leading-relaxed">
                <div className="font-semibold text-foreground mb-1">
                  MinfoMedia publishing desk
                </div>
                <p>
                  123 Main Street, Springfield, USA. All rights reserved. Registered under standard commercial publication agreements.
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* Bottom Editorial Separator */}
        <div className="mt-16 sm:mt-20">
          <Separator className="bg-border" />
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
            <div>&copy; {new Date().getFullYear()} MinfoMedia. All publication rights reserved.</div>
            <div className="flex items-center gap-4">
              <Link to="/PrivacyDataProtectionPage" className="hover:text-foreground transition-colors">
                Privacy policy
              </Link>
              <span>•</span>
              <Link to="/TermsConditionsPage" className="text-foreground font-semibold">
                Terms of use
              </Link>
              <span>•</span>
              <Link to="/AboutUsPage" className="hover:text-foreground transition-colors">
                About us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}